// Unit handling. Recipes keep the unit the author typed ("2 cups"); readers see
// amounts converted into their preferred system at display time.

export type UnitSystem = 'metric' | 'imperial';

export const MASS_UNITS = ['g', 'kg', 'oz', 'lb'] as const;
export const VOLUME_UNITS = ['ml', 'l', 'tsp', 'tbsp', 'cup', 'fl_oz', 'pint'] as const;
/** Units that don't map onto weight or volume, so they are never converted. */
export const OTHER_UNITS = ['pinch', 'clove', 'slice', 'can', 'bunch', 'sprig', 'handful', 'package'] as const;
export const UNITS = [...MASS_UNITS, ...VOLUME_UNITS, ...OTHER_UNITS] as const;

export type Unit = (typeof UNITS)[number];
export type Dimension = 'mass' | 'volume';

/** An amount of an ingredient. `unit: null` is a plain count ("2 eggs"). */
export interface Amount {
  quantity: number;
  unit: Unit | null;
}

interface Measure {
  dimension: Dimension;
  /** Grams (mass) or millilitres (volume) in one of this unit. */
  base: number;
  /** The preference that shows this unit as-is. Spoons read naturally in both. */
  system: UnitSystem | 'both';
}

const G_PER_OZ = 28.349523125;
const ML_PER_CUP = 236.5882365;

const MEASURES: { readonly [U in Unit]?: Measure } = {
  g: { dimension: 'mass', base: 1, system: 'metric' },
  kg: { dimension: 'mass', base: 1000, system: 'metric' },
  oz: { dimension: 'mass', base: G_PER_OZ, system: 'imperial' },
  lb: { dimension: 'mass', base: 453.59237, system: 'imperial' },
  ml: { dimension: 'volume', base: 1, system: 'metric' },
  l: { dimension: 'volume', base: 1000, system: 'metric' },
  tsp: { dimension: 'volume', base: 5, system: 'both' },
  tbsp: { dimension: 'volume', base: 15, system: 'both' },
  cup: { dimension: 'volume', base: ML_PER_CUP, system: 'imperial' },
  fl_oz: { dimension: 'volume', base: 29.5735295625, system: 'imperial' },
  pint: { dimension: 'volume', base: 473.176473, system: 'imperial' },
};

export function isUnit(value: unknown): value is Unit {
  return typeof value === 'string' && (UNITS as readonly string[]).includes(value);
}

export function unitDimension(unit: Unit | null): Dimension | null {
  return (unit && MEASURES[unit]?.dimension) || null;
}

/** Grams for mass, millilitres for volume; other units are returned unchanged. */
export function toBaseQuantity(amount: Amount): number {
  const measure = amount.unit ? MEASURES[amount.unit] : undefined;
  return measure ? amount.quantity * measure.base : amount.quantity;
}

export function baseUnit(dimension: Dimension): Unit {
  return dimension === 'mass' ? 'g' : 'ml';
}

/** Amounts in the same group can be added together. */
export function unitGroup(unit: Unit | null): string {
  if (unit === null) return 'count';
  return MEASURES[unit]?.dimension ?? unit;
}

export function scaleAmount(amount: Amount, factor: number): Amount {
  return factor === 1 ? amount : { quantity: amount.quantity * factor, unit: amount.unit };
}

/** Converts an amount into the units a reader prefers, picking a readable unit size. */
export function convertAmount(amount: Amount, system: UnitSystem): Amount {
  const measure = amount.unit ? MEASURES[amount.unit] : undefined;
  if (!measure) return amount;
  const base = amount.quantity * measure.base;

  if (measure.system === 'both') {
    // Spoons stay spoons until they get big: "6 tbsp" reads better as 90 ml or ⅜ cup.
    if (base > (system === 'metric' ? 75 : 59)) return bestAmount(measure.dimension, base, system);
    if (amount.unit === 'tsp' && amount.quantity >= 3) return { quantity: base / 15, unit: 'tbsp' };
    return amount;
  }
  if (measure.system === system) return tidyAmount(amount, base);
  return bestAmount(measure.dimension, base, system);
}

function tidyAmount(amount: Amount, base: number): Amount {
  switch (amount.unit) {
    case 'g':
    case 'kg':
    case 'oz':
    case 'lb':
      return bestAmount('mass', base, amount.unit === 'g' || amount.unit === 'kg' ? 'metric' : 'imperial');
    case 'ml':
    case 'l':
      return base >= 1000 ? { quantity: base / 1000, unit: 'l' } : { quantity: base, unit: 'ml' };
    case 'cup':
      return amount.quantity < 0.25 ? smallSpoons(base) : amount;
    default:
      return amount;
  }
}

function bestAmount(dimension: Dimension, base: number, system: UnitSystem): Amount {
  if (dimension === 'mass') {
    if (system === 'metric') return base >= 1000 ? { quantity: base / 1000, unit: 'kg' } : { quantity: base, unit: 'g' };
    const oz = base / G_PER_OZ;
    return oz >= 16 ? { quantity: oz / 16, unit: 'lb' } : { quantity: oz, unit: 'oz' };
  }
  if (system === 'metric') {
    if (base <= 75) return smallSpoons(base);
    return base >= 1000 ? { quantity: base / 1000, unit: 'l' } : { quantity: base, unit: 'ml' };
  }
  return base >= 59 ? { quantity: base / ML_PER_CUP, unit: 'cup' } : smallSpoons(base);
}

function smallSpoons(base: number): Amount {
  return base < 15 ? { quantity: base / 5, unit: 'tsp' } : { quantity: base / 15, unit: 'tbsp' };
}

const FRACTIONS: ReadonlyArray<readonly [number, string]> = [
  [1 / 8, '⅛'],
  [1 / 4, '¼'],
  [1 / 3, '⅓'],
  [3 / 8, '⅜'],
  [1 / 2, '½'],
  [5 / 8, '⅝'],
  [2 / 3, '⅔'],
  [3 / 4, '¾'],
  [7 / 8, '⅞'],
];

/** "1½", "⅔", "12½". Coarser fractions for bigger numbers. */
export function formatFraction(quantity: number, locale: string): string {
  if (!(quantity > 0)) return '0';
  const steps =
    quantity >= 10
      ? FRACTIONS.filter(([v]) => v === 1 / 2)
      : quantity >= 3
        ? FRACTIONS.filter(([v]) => [1 / 4, 1 / 3, 1 / 2, 2 / 3, 3 / 4].includes(v))
        : FRACTIONS;
  let whole = Math.floor(quantity);
  const rest = quantity - whole;
  let glyph = '';
  let bestDiff = rest;
  for (const [value, symbol] of steps) {
    const diff = Math.abs(rest - value);
    if (diff < bestDiff) {
      bestDiff = diff;
      glyph = symbol;
    }
  }
  if (1 - rest < bestDiff) {
    whole += 1;
    glyph = '';
  }
  if (whole === 0 && !glyph) glyph = steps[0]![1];
  return (whole ? new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(whole) : '') + glyph;
}

function roundMetric(quantity: number): number {
  if (quantity < 1) return Math.max(0.1, Math.round(quantity * 10) / 10);
  if (quantity < 10) return Math.round(quantity * 2) / 2;
  if (quantity < 100) return Math.round(quantity);
  return Math.round(quantity / 5) * 5;
}

/** Formats just the number part of an amount, e.g. "250", "1.5", "1½". */
export function formatQuantity(amount: Amount, locale: string): string {
  const { quantity, unit } = amount;
  switch (unit) {
    case 'g':
    case 'ml':
      return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(roundMetric(quantity));
    case 'kg':
    case 'l':
      return new Intl.NumberFormat(locale, { maximumFractionDigits: quantity < 10 ? 2 : 1 }).format(quantity);
    default:
      return formatFraction(quantity, locale);
  }
}

/**
 * Count to use when choosing a singular/plural unit label: "½ cup", "1 cup",
 * "1½ cups". Plural rules for fractional numbers vary by language, so anything
 * up to 1 is treated as one.
 */
export function pluralCount(quantity: number): number {
  return quantity <= 1 ? 1 : quantity;
}

export function displayTemperature(celsius: number, system: UnitSystem, oven = false): { value: number; unit: 'C' | 'F' } {
  if (system === 'metric') return { value: oven ? Math.round(celsius / 5) * 5 : Math.round(celsius), unit: 'C' };
  const fahrenheit = (celsius * 9) / 5 + 32;
  const step = oven ? 25 : 5;
  return { value: Math.round(fahrenheit / step) * step, unit: 'F' };
}

export function fahrenheitToCelsius(fahrenheit: number): number {
  return ((fahrenheit - 32) * 5) / 9;
}

const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

/** Imperial for US-style locales, metric everywhere else. */
export function defaultUnitSystem(locales: readonly string[] | undefined): UnitSystem {
  const first = locales?.[0];
  if (!first) return 'metric';
  try {
    const region = new Intl.Locale(first).maximize().region;
    return region && IMPERIAL_REGIONS.has(region) ? 'imperial' : 'metric';
  } catch {
    return 'metric';
  }
}
